
import Vue, { PropType } from "vue";
import { Toilet, ToiletType, ToiletStatus, Gateway } from "@/models";

import ImageUploader from "vue-image-upload-resize";
import BaseInputForm from "@/components/BaseInputForm.vue";
import BaseSelectorForm, { Option } from "@/components/BaseSelectorForm.vue";
import BaseSubmitButton from "@/components/BaseSubmitButton.vue";
import BaseCancelButton from "@/components/BaseCancelButton.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import BaseChuiGaki from "@/components/BaseChuiGaki.vue";

function convertToiletType(toiletType?: string): ToiletType {
  switch (toiletType) {
    case "UNKNOWNTOILETTYPE":
      return ToiletType.UNKNOWN_TOILET_TYPE;
    case "UNKNOWN_TOILET_TYPE":
      return ToiletType.UNKNOWN_TOILET_TYPE;
    case "FOR_MAN":
      return ToiletType.FOR_MAN;
    case "FOR_WOMAN":
      return ToiletType.FOR_WOMAN;
    case "UNISEX":
      return ToiletType.UNISEX;
    case "MULTIPURPOSE":
      return ToiletType.MULTI_PURPOSE;
    case "MULTI_PURPOSE":
      return ToiletType.MULTI_PURPOSE;
    default:
      return ToiletType.UNKNOWN_TOILET_TYPE;
  }
}

function convertToiletStatus(status?: string): ToiletStatus {
  switch (status) {
    case "UNKNOWNTOILETSTATUS":
      return ToiletStatus.UNKNOWNTOILETSTATUS;
    case "RUNNING":
      return ToiletStatus.RUNNING;
    case "INMAINTENANCE":
      return ToiletStatus.IN_MAINTENANCE;
    case "IN_MAINTENANCE":
      return ToiletStatus.IN_MAINTENANCE;
    default:
      return ToiletStatus.UNKNOWNTOILETSTATUS;
  }
}

export type DataType = {
  floor: string;
  toiletType: string;
  name: string;
  gatewayMac: string;
  gatewayId: string;
  status: string;
  typeOptions: Array<Option>;
  statusOptions: Array<Option>;
  image: Blob | undefined;
  temporalImageUrl: string | undefined;
  imageUploaderKey: boolean;
  // コンポーネントを強制的に再描画させるためのキー。
  // 再描画させないと、「アップロードした画像をユーザーが削除し同じ画像をあげようとした時」にイベントが発生せず、その画像をあげられなくなってしまう。
  // 使ってるライブラリというより input タグがそういう挙動をするのでしょうがない。
};

export default Vue.extend({
  name: "ToiletEditor",

  components: {
    BaseChuiGaki,
    BaseInputForm,
    BaseSelectorForm,
    BaseSubmitButton,
    BaseCancelButton,
    ValidationProvider,
    ImageUploader,
    ValidationObserver,
  },

  props: {
    toilet: Object as PropType<Toilet>,
    gateways: Array as PropType<Array<Gateway>>,
  },

  data(): DataType {
    return {
      floor: "",
      toiletType: "",
      name: "",
      gatewayMac: "",
      gatewayId: "",
      status: "",

      typeOptions: [
        {
          name: "男性トイレ",
          value: ToiletType.FOR_MAN,
        },
        {
          name: "女性トイレ",
          value: ToiletType.FOR_WOMAN,
        },
        {
          name: "多目的トイレ",
          value: ToiletType.MULTI_PURPOSE,
        },
        {
          name: "共用",
          value: ToiletType.UNISEX,
        },
        {
          name: "授乳室",
          value: ToiletType.NURSING_ROOM,
        },
        {
          name: "パウダーコーナー",
          value: ToiletType.POWDER_CORNER,
        },
      ],

      statusOptions: [
        {
          name: "メンテ中",
          value: ToiletStatus.IN_MAINTENANCE,
        },
        {
          name: "稼動",
          value: ToiletStatus.RUNNING,
        },
      ],
      image: undefined,
      temporalImageUrl: undefined,
      imageUploaderKey: false,
    };
  },

  computed: {
    canEditStatus(): boolean {
      return !!this.toilet?.id;
    },

    isEdited(): boolean {
      return (
        this.toilet.floor !== this.floor ||
        this.toilet.toiletType !== this.toiletType ||
        this.toilet.name !== this.name ||
        this.toilet.status !== this.status ||
        (this.toilet.gatewayMac !== this.gatewayMac && this.gatewayMac !== "")
      );
    },

    canSubmit(): boolean {
      return !!this.toiletType && this.gatewayMac !== "";
    },

    gatewayOptions(): Array<Option> {
      const gatewayOptionsList = this.gateways.map((g): Option => {
        return { name: g.mac || "", value: g.mac || "" };
      });
      if (this.toilet?.gatewayMac) {
        gatewayOptionsList.push({
          name: this.toilet.gatewayMac || "",
          value: this.toilet.gatewayMac || "",
        });
      }
      return gatewayOptionsList;
    },
    hasImage(): boolean {
      return this.toilet?.floorPlanUrl !== undefined || this.hasNewImage;
    },
    hasNewImage(): boolean {
      return this.image !== undefined;
    },
    displayFileName(): string {
      if (this.toilet?.floorPlanUrl !== undefined) {
        const splittedResourceName: string[] = decodeURI(this.toilet.floorPlanUrl).split("/");
        const tailResourceName: string = splittedResourceName[splittedResourceName.length - 1];
        return tailResourceName;
      }
      return "";
    },
  },
  watch: {
    toilet: function (n: Toilet) {
      this.floor = n.floor || "";
      this.toiletType = n.toiletType || "";
      this.name = n.name || "";
      this.gatewayMac = n.gatewayMac || "";
      this.status = n.status || "";
    },
  },

  methods: {
    setImage: function (output: Blob) {
      this.image = output;
    },
    backGroundImageStyleText(): string {
      if (this.toilet?.floorPlanUrl !== undefined) {
        return `background-image: url(${this.toilet.floorPlanUrl}); background-size: cover;`;
      }

      if (this.hasNewImage) {
        // このコンポーネントが破棄されたら URL のインスタンスも破棄するようにするため、ローカル変数ではなく this に持たせた。
        this.temporalImageUrl = URL.createObjectURL(this.image);
        return `background-image: url(${this.temporalImageUrl}); background-size: cover;`;
      }
      return "";
    },
    saveToilet: function (): void {
      if (!this.canSubmit) {
        return;
      }

      this.$emit(
        "save-toilet",
        new Toilet({
          name: this.name,
          floor: this.floor,
          gatewayMac: this.gatewayMac,
          gatewayId: this.gateways.find((gateway) => gateway.mac === this.gatewayMac)?.id,
          toiletType: convertToiletType(this.toiletType),
          status: convertToiletStatus(this.status),
        }),
        this.image,
      );

      // FIXME: destroy に書いておけばいらない？
      if (this.temporalImageUrl !== undefined) {
        URL.revokeObjectURL(this.temporalImageUrl);
      }
    },
    deleteImage(): void {
      if (this.toilet?.floorPlanUrl !== undefined) {
        this.toilet.floorPlanUrl = undefined;
      }
      this.image = undefined;
      this.imageUploaderKey = !this.imageUploaderKey; // 再描画させるのが目的なので toggle させるだけでよい。

      this.$emit("delete-image");
    },
  },
  destroyed(): void {
    if (this.temporalImageUrl !== undefined) {
      URL.revokeObjectURL(this.temporalImageUrl);
    }
  },
});
